
import { AfterViewInit, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from "primeng/button";
import { InputText } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { GotoBlocklyDirective } from "src/app/shared/directives/goto-blockly.directive";
import { EditorItemsService } from 'src/app/shared/services/editor-items.service';
import { FusionService } from '@shared-angular/services/fusion.service';
import { getFusionBotTypeOptions } from '../fusionbot-tab.utils';

import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { SelectButton } from "primeng/selectbutton";
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectButtonModule } from 'primeng/selectbutton';

@Component({
  selector: 'app-fusion-miro-by-template',
  imports: [FormsModule, SelectModule, ButtonModule, InputGroupModule, InputGroupAddonModule, GotoBlocklyDirective, InputText, SelectButton, ToggleSwitchModule, RadioButtonModule, SelectButtonModule],
  templateUrl: './fusion-miro-by-template.component.html',
  styleUrl: './fusion-miro-by-template.component.scss'
})
export class FusionMiroByTemplateComponent implements AfterViewInit {
  public editorItemsService = inject(EditorItemsService);
  public fusionService = inject(FusionService);
  private sanitizer = inject(DomSanitizer);
  protected iframeUrl?: SafeResourceUrl;
  protected get botTypeOptions() {
    return getFusionBotTypeOptions(this.fusionService.loginService.systemConf);
  }
  protected systemLanguageOptions = [
    { label: $localize`:@@fusionconfig-systemlang-de:Deutsch`, value: 'de' },
    { label: $localize`:@@fusionconfig-systemlang-en:Englisch`, value: 'en' },
    { label: $localize`:@@fusionconfig-systemlang-fr:Französisch`, value: 'fr' },
    { label: $localize`:@@fusionconfig-systemlang-es:Spanisch`, value: 'es' },
    { label: $localize`:@@fusionconfig-systemlang-it:Italienisch`, value: 'it' },
    { label: $localize`:@@fusionconfig-systemlang-nl:Niederländisch`, value: 'nl' },
    { label: $localize`:@@fusionconfig-systemlang-nb:Norwegisch`, value: 'nb-no' },
    { label: $localize`:@@fusionconfig-systemlang-fr-be:Französisch (Belgien)`, value: 'fr-be' },
    { label: $localize`:@@fusionconfig-systemlang-nl-be:Niederländisch (Belgien)`, value: 'nl-be' },
    { label: $localize`:@@fusionconfig-systemlang-pl:Polnisch`, value: 'pl' },
    { label: $localize`:@@fusionconfig-systemlang-pt:Portugiesisch`, value: 'pt' },
    { label: $localize`:@@fusionconfig-systemlang-hu:Ungarisch`, value: 'hu' },
    { label: $localize`:@@fusionconfig-systemlang-ro:Rumänisch`, value: 'ro' },
    { label: $localize`:@@fusionconfig-systemlang-tr:Türkisch`, value: 'tr' },
    { label: $localize`:@@fusionconfig-systemlang-fi:Finnisch`, value: 'fi' },
    { label: $localize`:@@fusionconfig-systemlang-sv:Schwedisch`, value: 'sv' },
    { label: $localize`:@@fusionconfig-systemlang-da:Dänisch`, value: 'da' },
    { label: $localize`:@@fusionconfig-systemlang-cs:Tschechisch`, value: 'cs' },
    { label: $localize`:@@fusionconfig-systemlang-el:Griechisch`, value: 'el' }
  ];
  protected viewModeOptions = [
    { label: $localize`:@@fusionconfig-viewmode-split:Split (Side-by-Side)`, value: 'split' },
    { label: $localize`:@@fusionconfig-viewmode-split-reverse:Split Reverse (Side-by-Side)`, value: 'split-reverse' },
    { label: $localize`:@@fusionconfig-viewmode-window:Fenster (Overlay)`, value: 'window' },
    { label: $localize`:@@fusionconfig-viewmode-full:Vollbild`, value: 'full' },
    { label: $localize`:@@fusionconfig-viewmode-float:Floating (Chat-Bubble)`, value: 'float' }
  ];
  private readonly miroUrlRegex = new RegExp("^https://miro.com/app/board/([a-zA-Z0-9=_-]+)/?$");
  private readonly miroIdRegex = new RegExp("^[a-zA-Z0-9=_-]{10,50}$");

  public get idInvalid(): boolean {
    if (this.fusionService.selectedMiroByTemplate) {
      if (this.miroIdRegex.test(this.fusionService.selectedMiroByTemplate.metadata.templateId)) {
        return false;
      }
    }
    return true;
  };

  public get booleanOptions(): { label: string; value: boolean }[] {
    return [
      {
        label: $localize`:@@off:Aus`,
        value: false
      },
      {
        label: $localize`:@@on:An`,
        value: true
      }
    ];
  }

  public ngAfterViewInit(): void {
    if (!this.idInvalid) {
      this.setIframeUrlFromSettings();
    }
  }

  public boardUrlInputChange(value: string) {
    if (this.fusionService.selectedMiroByTemplate) {
      if (this.miroUrlRegex.test(value)) {
        const boardId = value.slice(27, -1);
        value = boardId;
        this.fusionService.selectedMiroByTemplate.metadata.templateId = value;
        void this.fusionService.updateFusionField('templateId', value);
      } else if (this.miroIdRegex.test(value)) {
        this.fusionService.selectedMiroByTemplate.metadata.templateId = value;
        void this.fusionService.updateFusionField('templateId', value);
      } else if (value === "") {
        this.fusionService.selectedMiroByTemplate.metadata.templateId = value;
        void this.fusionService.updateFusionField('templateId', value);
      }
      this.setIframeUrlFromSettings();
    }
  }

  public setIframeUrlFromSettings() {
    if (!this.fusionService.selectedMiroByTemplate) return;
    const url = new URL(`https://miro.com/app/live-embed/${this.fusionService.selectedMiroByTemplate.metadata.templateId}`);
    if (!this.fusionService.selectedMiroByTemplate.metadata.editadble) {
      url.searchParams.append("embedMode", "view_only_without_ui");
    }
    if (this.fusionService.selectedMiroByTemplate.metadata.autoplay) {
      url.searchParams.append("autoplay", "true");
    }
    this.iframeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url.href);
  }

}
